// Pricing.js (Client Pricing / Projects Page Component)

import React from 'react';
import styled from 'styled-components';
import { IoMdPeople, IoIosCut, IoIosArrowForward, IoIosCalendar } from "react-icons/io";
import { IconContext } from 'react-icons/lib';

const PageWrapper = styled.div`
  background: radial-gradient(circle at 50% 0%, #151a2e 0%, #0a0d16 100%);
  color: #e2e8f0;
  min-height: 100vh;
  font-family: 'Inter', sans-serif;
  padding: 80px 20px 100px;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Heading = styled.h1`
  font-family: 'Outfit', sans-serif;
  font-size: 3rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: 15px;
  background: linear-gradient(135deg, #ffffff 30%, #a5b4fc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const Subtitle = styled.p`
  color: #94a3b8;
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 30px;
  justify-content: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const ProductCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 35px;
  backdrop-filter: blur(12px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 520px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #818cf8, #4f46e5);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-8px);
    border-color: rgba(129, 140, 248, 0.3);
    background: rgba(255, 255, 255, 0.035);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3);

    &::before {
      opacity: 1;
    }
  }
`;

const CardTop = styled.div`
  display: flex;
  flex-direction: column;
`;

const IconWrapper = styled.div`
  width: 65px;
  height: 65px;
  border-radius: 16px;
  background: rgba(129, 140, 248, 0.1);
  border: 1px solid rgba(129, 140, 248, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #818cf8;
  margin-bottom: 25px;
  transition: all 0.3s ease;

  ${ProductCard}:hover & {
    background: rgba(129, 140, 248, 0.2);
    color: #a5b4fc;
    transform: scale(1.05);
  }
`;

const PlanTitle = styled.h3`
  font-family: 'Outfit', sans-serif;
  font-size: 1.6rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 6px;
`;

const CategoryTag = styled.span`
  align-self: flex-start;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #818cf8;
  background: rgba(129, 140, 248, 0.12);
  padding: 4px 12px;
  border-radius: 20px;
  margin-bottom: 20px;
`;

const Description = styled.p`
  font-size: 0.95rem;
  color: #94a3b8;
  line-height: 1.6;
  margin-bottom: 25px;
  min-height: 75px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 30px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  padding-top: 25px;
`;

const FeatureItem = styled.li`
  font-size: 0.9rem;
  color: #cbd5e1;
  display: flex;
  align-items: center;
  gap: 10px;

  &::before {
    content: '✓';
    color: #10b981;
    font-weight: 700;
  }
`;

const ActionButton = styled.button`
  background: linear-gradient(135deg, #4b59f7 0%, #0467fb 100%);
  color: #fff;
  border: none;
  padding: 14px 24px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(75, 89, 247, 0.25);
  width: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(75, 89, 247, 0.4);
    background: linear-gradient(135deg, #5c6aff 0%, #1e7aff 100%);
  }

  &:active {
    transform: translateY(0);
  }
`;

function ClientPricing() {
  return (
    <IconContext.Provider value={{ size: '28px' }}>
      <PageWrapper>
        <ContentContainer>
          <HeaderSection>
            <Heading>Our Clients & Portals</Heading>
            <Subtitle>
              Discover bespoke applications and digital web portals developed for our valued community partners.
            </Subtitle>
          </HeaderSection>

          <Grid>
            <ProductCard>
              <CardTop>
                <IconWrapper>
                  <IoMdPeople />
                </IconWrapper>
                <PlanTitle>Matrimonial Portal Plan</PlanTitle>
                <CategoryTag>Nair Community</CategoryTag>
                <Description>
                  A specialized wedding matchmaking portal designed and built in collaboration with the Nair Service Society Delhi.
                </Description>
              </CardTop>
              <div>
                <FeatureList>
                  <FeatureItem>Wedding Matchmaking Directory</FeatureItem>
                  <FeatureItem>Mobile App Compatibility</FeatureItem>
                  <FeatureItem>Privacy-First Match Finder</FeatureItem>
                </FeatureList>
                <ActionButton onClick={() => window.location.href = "/Blog/#Matrimonial"}>
                  View Project Details <IoIosArrowForward />
                </ActionButton>
              </div>
            </ProductCard>

            <ProductCard>
              <CardTop>
                <IconWrapper>
                  <IoIosCut />
                </IconWrapper>
                <PlanTitle>Marie Claire Salon</PlanTitle>
                <CategoryTag>Web Portal</CategoryTag>
                <Description>
                  A comprehensive web portal serving as a single point of access to booking services, resources, and salon outlets.
                </Description>
              </CardTop>
              <div>
                <FeatureList>
                  <FeatureItem>User Friendly, Interactive UI</FeatureItem>
                  <FeatureItem>Custom Web Portal Setup</FeatureItem>
                  <FeatureItem>Service Catalog & Booker</FeatureItem>
                </FeatureList>
                <ActionButton onClick={() => window.location.href = "/Blog/#Marie"}>
                  View Project Details <IoIosArrowForward />
                </ActionButton>
              </div>
            </ProductCard>

            <ProductCard>
              <CardTop>
                <IconWrapper>
                  <IoIosCalendar />
                </IconWrapper>
                <PlanTitle>Plannit</PlanTitle>
                <CategoryTag>Event Booking</CategoryTag>
                <Description>
                  A social dining and event curation platform that connects like-minded people at top-tier venues for real, face-to-face connections.
                </Description>
              </CardTop>
              <div>
                <FeatureList>
                  <FeatureItem>Personality-Based Grouping</FeatureItem>
                  <FeatureItem>Automated Table Reservations</FeatureItem>
                  <FeatureItem>Interactive Icebreakers Included</FeatureItem>
                </FeatureList>
                <ActionButton onClick={() => window.location.href ="/Blog/#Plannit"}>
                  View Project Details <IoIosArrowForward />
                </ActionButton>
              </div>
            </ProductCard>
          </Grid>
        </ContentContainer>
      </PageWrapper>
    </IconContext.Provider>
  );
}

export default ClientPricing;
